import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sun, Factory, TrendingUp, Shield, Zap, CheckCircle2, ArrowRight, Building2, Leaf, Euro, Clock, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const IndustriePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    firma: '',
    ansprechpartner: '',
    email: '',
    telefon: '',
    dachflaeche: '',
    stromverbrauch: '',
    nachricht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'industrie',
          ...formData
        })
      });
      
      if (response.ok) {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    
    setIsSubmitting(false);
  };

  const t = {
    de: {
      badge: 'Großanlagen ab 150 kWp',
      heroTitle: 'Industrielle',
      heroTitle2: 'Photovoltaik',
      heroDesc: 'Maßgeschneiderte Solarlösungen für Industrie, Gewerbe und Logistik. Von der Planung bis zur schlüsselfertigen Übergabe – alles aus einer Hand.',
      freeConsult: 'Kostenlose Projektberatung',
      viewRefs: 'Referenzen ansehen',
      mwpInstalled: 'MWp installiert',
      indProjects: 'Industrieprojekte',
      avgSavings: 'Ø Ersparnis',
      whyPV: 'Warum Photovoltaik für Ihr',
      business: 'Unternehmen?',
      investFuture: 'Investieren Sie in die Zukunft Ihres Unternehmens mit nachhaltiger Energie',
      costSaving: 'Massive Kosteneinsparung',
      costSavingDesc: 'Reduzieren Sie Ihre Stromkosten um bis zu 80%. Bei Großanlagen amortisiert sich die Investition oft in unter 5 Jahren.',
      co2: 'CO2-Neutralität',
      co2Desc: 'Erreichen Sie Ihre Klimaziele und verbessern Sie Ihr Unternehmensimage mit grüner Energie.',
      valueInc: 'Wertsteigerung',
      valueIncDesc: 'Steigern Sie den Wert Ihrer Immobilie und sichern Sie sich langfristige Energieunabhängigkeit.',
      planSec: 'Planungssicherheit',
      planSecDesc: 'Schützen Sie sich vor steigenden Strompreisen mit eigenem Solarstrom für die nächsten 30+ Jahre.',
      systemSizes: 'Anlagengrößen',
      keyData: '& Eckdaten',
      midsize: 'Mittelstand',
      industry: 'Industrie',
      largeProj: 'Großprojekte',
      mostPopular: 'BELIEBTESTE WAHL',
      idealFor: 'Ideal für:',
      production: 'Produktionsbetriebe',
      logistics: 'Logistikzentren',
      retail: 'Einzelhandel',
      agriculture: 'Landwirtschaft',
      indComplex: 'Industriekomplexe',
      airports: 'Flughäfen',
      serviceRange: 'Unser Leistungsspektrum',
      turnkey: 'Schlüsselfertige Komplettlösungen für Ihr Solarprojekt',
      planning: 'Planung & Engineering',
      installation: 'Installation & Montage',
      service: 'Service & Wartung',
      siteAnalysis: 'Standortanalyse',
      yieldCalc: 'Ertragsberechnung',
      statics: 'Statikprüfung',
      visualization: '3D-Visualisierung',
      permits: 'Genehmigungsmanagement',
      roofGround: 'Dach- und Freiflächenanlagen',
      carport: 'Carport-Systeme',
      facade: 'Fassadenintegration',
      trafo: 'Trafostation',
      gridConn: 'Netzanschluss',
      monitoring: '24/7 Monitoring',
      remote: 'Fernüberwachung',
      maintenance: 'Wartungsverträge',
      cleaning: 'Reinigung',
      warranty: 'Garantieabwicklung',
      ourRefs: 'Unsere Referenzen',
      successProj: 'Erfolgreich realisierte Großprojekte',
      power: 'Leistung',
      savings: 'Ersparnis',
      readyEnergy: 'Bereit für Ihre Energiewende?',
      readyDesc: 'Lassen Sie uns gemeinsam Ihr Solarprojekt planen. Kostenlose Erstberatung und individuelle Angebote.',
      projectReq: 'Projektanfrage stellen',
      projectReqDesc: 'Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden',
      company: 'Firma',
      contact: 'Ansprechpartner',
      roofArea: 'Geschätzte Dachfläche (m²)',
      powerCons: 'Jahres-Stromverbrauch (kWh)',
      message: 'Nachricht (optional)',
      yourProject: 'Beschreiben Sie Ihr Projekt...',
      submit: 'Projektanfrage absenden',
      sending: 'Wird gesendet...',
      reqReceived: 'Anfrage erhalten!',
      thankYou: 'Vielen Dank für Ihr Interesse. Unser Industrie-Team wird sich innerhalb von 24 Stunden bei Ihnen melden.',
      backHome: 'Zurück zur Startseite',
      orCall: 'Oder rufen Sie uns an:',
      logisticsCenter: 'Logistikzentrum München',
      productionHall: 'Produktionshalle Stuttgart',
      indPlant: 'Industrieanlage Zürich'
    },
    en: {
      badge: 'Large-scale systems from 150 kWp',
      heroTitle: 'Industrial',
      heroTitle2: 'Photovoltaics',
      heroDesc: 'Customized solar solutions for industry, commerce and logistics. From planning to turnkey handover – everything from one source.',
      freeConsult: 'Free Project Consultation',
      viewRefs: 'View References',
      mwpInstalled: 'MWp installed',
      indProjects: 'Industrial Projects',
      avgSavings: 'Avg. Savings',
      whyPV: 'Why Photovoltaics for Your',
      business: 'Business?',
      investFuture: 'Invest in the future of your company with sustainable energy',
      costSaving: 'Massive Cost Savings',
      costSavingDesc: 'Reduce your electricity costs by up to 80%. Large-scale systems often pay for themselves in under 5 years.',
      co2: 'CO2 Neutrality',
      co2Desc: 'Achieve your climate goals and improve your company image with green energy.',
      valueInc: 'Value Increase',
      valueIncDesc: 'Increase the value of your property and secure long-term energy independence.',
      planSec: 'Planning Security',
      planSecDesc: 'Protect yourself from rising electricity prices with your own solar power for the next 30+ years.',
      systemSizes: 'System Sizes',
      keyData: '& Key Data',
      midsize: 'Medium Business',
      industry: 'Industry',
      largeProj: 'Large Projects',
      mostPopular: 'MOST POPULAR',
      idealFor: 'Ideal for:',
      production: 'Manufacturing',
      logistics: 'Logistics Centers',
      retail: 'Retail',
      agriculture: 'Agriculture',
      indComplex: 'Industrial Complexes',
      airports: 'Airports',
      serviceRange: 'Our Service Range',
      turnkey: 'Turnkey complete solutions for your solar project',
      planning: 'Planning & Engineering',
      installation: 'Installation & Assembly',
      service: 'Service & Maintenance',
      siteAnalysis: 'Site Analysis',
      yieldCalc: 'Yield Calculation',
      statics: 'Structural Analysis',
      visualization: '3D Visualization',
      permits: 'Permit Management',
      roofGround: 'Roof & Ground Systems',
      carport: 'Carport Systems',
      facade: 'Facade Integration',
      trafo: 'Transformer Station',
      gridConn: 'Grid Connection',
      monitoring: '24/7 Monitoring',
      remote: 'Remote Monitoring',
      maintenance: 'Maintenance Contracts',
      cleaning: 'Cleaning',
      warranty: 'Warranty Processing',
      ourRefs: 'Our References',
      successProj: 'Successfully realized large-scale projects',
      power: 'Power',
      savings: 'Savings',
      readyEnergy: 'Ready for Your Energy Transition?',
      readyDesc: "Let's plan your solar project together. Free initial consultation and individual offers.",
      projectReq: 'Submit Project Inquiry',
      projectReqDesc: 'Fill out the form and we will contact you within 24 hours',
      company: 'Company',
      contact: 'Contact Person',
      roofArea: 'Estimated Roof Area (m²)',
      powerCons: 'Annual Power Consumption (kWh)',
      message: 'Message (optional)',
      yourProject: 'Describe your project...',
      submit: 'Submit Project Inquiry',
      sending: 'Sending...',
      reqReceived: 'Inquiry Received!',
      thankYou: 'Thank you for your interest. Our industrial team will contact you within 24 hours.',
      backHome: 'Back to Homepage',
      orCall: 'Or call us:',
      logisticsCenter: 'Logistics Center Munich',
      productionHall: 'Production Hall Stuttgart',
      indPlant: 'Industrial Plant Zurich'
    }
  };

  const txt = t[language] || t.de;

  const vorteile = [
    { icon: Euro, title: txt.costSaving, description: txt.costSavingDesc },
    { icon: Leaf, title: txt.co2, description: txt.co2Desc },
    { icon: TrendingUp, title: txt.valueInc, description: txt.valueIncDesc },
    { icon: Shield, title: txt.planSec, description: txt.planSecDesc }
  ];

  const leistungen = [
    { title: txt.planning, items: [txt.siteAnalysis, txt.yieldCalc, txt.statics, txt.visualization, txt.permits] },
    { title: txt.installation, items: [txt.roofGround, txt.carport, txt.facade, txt.trafo, txt.gridConn] },
    { title: txt.service, items: [txt.monitoring, txt.remote, txt.maintenance, txt.cleaning, txt.warranty] }
  ];

  const referenzen = [
    { image: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e?w=800&q=80', title: txt.logisticsCenter, leistung: '850 kWp', ersparnis: '320.000 €/' + (language === 'en' ? 'Year' : 'Jahr') },
    { image: 'https://images.unsplash.com/photo-1726866672851-5b99c837603c?w=800&q=80', title: txt.productionHall, leistung: '1.2 MWp', ersparnis: '480.000 €/' + (language === 'en' ? 'Year' : 'Jahr') },
    { image: 'https://customer-assets.emergentagent.com/job_gravante-app-1/artifacts/urllb5bi_PHOTO-2025-12-09-11-35-47.jpg', title: txt.indPlant, leistung: '1 MWp', ersparnis: '380.000 €/' + (language === 'en' ? 'Year' : 'Jahr') }
  ];

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-40 pb-20">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-lg mx-auto">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{txt.reqReceived}</h2>
              <p className="text-gray-600 mb-8">{txt.thankYou}</p>
              <Button onClick={() => navigate('/')} className="bg-sky-600 hover:bg-sky-700">{txt.backHome}</Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen pt-32 lg:pt-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1756913455724-6b3a2ae0130b?w=1920&q=80" alt="Industrial Solar" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-amber-500/20 backdrop-blur-md text-amber-300 rounded-full text-sm font-medium mb-6 border border-amber-500/30">
              <Factory className="w-4 h-4 mr-2" />
              {txt.badge}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {txt.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-sky-400">{txt.heroTitle2}</span>
            </h1>
            
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">{txt.heroDesc}</p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })} size="lg" className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 text-lg">
                {txt.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button onClick={() => document.getElementById('referenzen').scrollIntoView({ behavior: 'smooth' })} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                {txt.viewRefs}
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/20">
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">50+</div>
                <div className="text-sm text-gray-300">{txt.mwpInstalled}</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">200+</div>
                <div className="text-sm text-gray-300">{txt.indProjects}</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">30%</div>
                <div className="text-sm text-gray-300">{txt.avgSavings}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {txt.whyPV}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-sky-600">{txt.business}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{txt.investFuture}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vorteile.map((vorteil, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-sky-100 rounded-xl flex items-center justify-center mb-6">
                    <vorteil.icon className="w-7 h-7 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{vorteil.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{vorteil.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Anlagengrößen Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {txt.systemSizes}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-sky-600">{txt.keyData}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-amber-400 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{txt.midsize}</h3>
                <div className="text-4xl font-bold text-amber-500 mb-4">150-500 kWp</div>
                <ul className="text-left space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />{txt.idealFor}</li>
                  <li className="pl-6">• {txt.production}</li>
                  <li className="pl-6">• {txt.logistics}</li>
                  <li className="pl-6">• {txt.retail}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-400 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-amber-500 text-black text-xs font-bold px-4 py-1 rounded-full">{txt.mostPopular}</span>
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Factory className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{txt.industry}</h3>
                <div className="text-4xl font-bold text-amber-500 mb-4">500 kWp - 2 MWp</div>
                <ul className="text-left space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />{txt.idealFor}</li>
                  <li className="pl-6">• {txt.indComplex}</li>
                  <li className="pl-6">• {txt.logistics}</li>
                  <li className="pl-6">• {txt.agriculture}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-amber-400 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sun className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{txt.largeProj}</h3>
                <div className="text-4xl font-bold text-sky-500 mb-4">2+ MWp</div>
                <ul className="text-left space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />{txt.idealFor}</li>
                  <li className="pl-6">• {txt.indComplex}</li>
                  <li className="pl-6">• {txt.airports}</li>
                  <li className="pl-6">• {txt.logistics}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leistungsspektrum */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{txt.serviceRange}</h2>
            <p className="text-lg text-gray-600">{txt.turnkey}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leistungen.map((leistung, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">{index + 1}</span>
                    {leistung.title}
                  </h3>
                  <ul className="space-y-3">
                    {leistung.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-3" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Referenzen */}
      <section id="referenzen" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{txt.ourRefs}</h2>
            <p className="text-lg text-gray-600">{txt.successProj}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {referenzen.map((ref, index) => (
              <Card key={index} className="overflow-hidden border-0 shadow-lg group">
                <div className="relative h-64">
                  <img src={ref.image} alt={ref.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-bold mb-2">{ref.title}</h3>
                    <div className="flex justify-between text-sm">
                      <span>{txt.power}: {ref.leistung}</span>
                      <span>{txt.savings}: {ref.ersparnis}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Form */}
      <section id="kontakt" className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">{txt.readyEnergy}</h2>
              <p className="text-xl text-gray-300 mb-8">{txt.readyDesc}</p>
              <div className="flex items-center gap-4">
                <Phone className="w-6 h-6 text-amber-400" />
                <div>
                  <p className="text-sm text-gray-400">{txt.orCall}</p>
                  <a href="tel:+4915782283963" className="text-xl font-bold hover:text-amber-400">+49 157 82283963</a>
                </div>
              </div>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{txt.projectReq}</h3>
                <p className="text-gray-600 mb-6">{txt.projectReqDesc}</p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{txt.company} *</label>
                      <input type="text" required value={formData.firma} onChange={(e) => setFormData({...formData, firma: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{txt.contact} *</label>
                      <input type="text" required value={formData.ansprechpartner} onChange={(e) => setFormData({...formData, ansprechpartner: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'en' ? 'Phone' : 'Telefon'}</label>
                      <input type="tel" value={formData.telefon} onChange={(e) => setFormData({...formData, telefon: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{txt.roofArea}</label>
                      <input type="text" value={formData.dachflaeche} onChange={(e) => setFormData({...formData, dachflaeche: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{txt.powerCons}</label>
                      <input type="text" value={formData.stromverbrauch} onChange={(e) => setFormData({...formData, stromverbrauch: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{txt.message}</label>
                    <textarea rows={3} value={formData.nachricht} onChange={(e) => setFormData({...formData, nachricht: e.target.value})} placeholder={txt.yourProject} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"></textarea>
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 text-lg">
                    {isSubmitting ? txt.sending : txt.submit}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                  <a href="mailto:Buchhaltung@gravante.de" className="inline-flex items-center justify-center px-8 py-4 bg-white text-black rounded-lg font-bold hover:bg-gray-100 transition-colors border border-gray-200">
                    <Mail className="w-5 h-5 mr-2" />
                    Buchhaltung@gravante.de
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriePage;
